import { CloudOff, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { inFrame } from "../lib/api";
import type { CompanySummary } from "../lib/queries";

/**
 * Shown above every page while the ServiceM8 connection isn't working. "error" is a temporary
 * failure the server keeps retrying; "revoked" (or never connected) needs someone to reconnect,
 * as does a connection missing permissions the app now asks for (e.g. a tool added since).
 * Updates live: the server publishes company.changed whenever the status changes.
 */
export function ConnectionBanner({ company }: Readonly<{ company: CompanySummary }>) {
    const status = company.connection?.status ?? "revoked";
    const missing = company.connection?.missingScopes ?? [];

    if (status === "active" && missing.length) {
        return (
            <Banner company={company} title="ServiceM8 needs to be reconnected to allow new features.">
                The app now uses ServiceM8 permissions this account hasn't granted yet ({missing.join(", ")}), so some data
                can't be synced.
            </Banner>
        );
    }
    if (status === "active") return null;

    if (status === "error") {
        return (
            <output className="flex items-start gap-2 bg-surface-2 px-4 py-2 text-sm text-muted">
                <CloudOff className="mt-px size-4" />
                <span>Having trouble reaching ServiceM8, retrying automatically. Data may be a few minutes behind.</span>
            </output>
        );
    }

    return (
        <Banner company={company} title="ServiceM8 needs to be reconnected.">
            Changes can't be saved to ServiceM8 and data isn't being synced.
        </Banner>
    );
}

/** "Reconnect" strip: a Reconnect button for managers, who to ask for everyone else. */
function Banner({ company, title, children }: Readonly<{ company: CompanySummary; title: string; children: ReactNode }>) {
    return (
        <div role="alert" className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-warning-soft px-4 py-2 text-sm text-warning-ink">
            <TriangleAlert className="size-4" />
            <span className="min-w-0 flex-1">
                <strong>{title}</strong> {children} {!company.canManage && "Ask a manager to reconnect it."}
            </span>
            {company.canManage && <ReconnectButton />}
        </div>
    );
}

/** Reconnect ServiceM8 (managers): asks again for every permission the app uses. */
export function ReconnectButton({ className = "btn-primary px-3 py-1" }: Readonly<{ className?: string }>) {
    // ServiceM8's sign-in page won't load inside ServiceM8, so from there it opens a tab
    // (and comes back to ServiceM8); in the browser it returns to Settings › ServiceM8.
    return (
        <a href={inFrame ? "/servicem8/connect" : "/servicem8/connect?from=web"} target={inFrame ? "_blank" : undefined} rel="noreferrer" className={className}>
            Reconnect
        </a>
    );
}
